import { ReactNode, useEffect, useMemo, memo } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { useLanguage } from '@/context/LanguageContext';
import ErrorBoundary from '@/components/shared/ErrorBoundary';
import SessionTimeoutWarning from '@/components/security/SessionTimeoutWarning';
import { LayoutProvider } from '@/context/LayoutContext';

interface LayoutProps {
  children: ReactNode;
}

const Layout = memo(({ children }: LayoutProps) => {
  const location = useLocation();
  const { language } = useLanguage();
  
  // Create a memoized key that only changes when language or route changes
  const contentKey = useMemo(() => `${language}-${location.pathname}`, [language, location.pathname]);
  
  // Log route and language changes for debugging
  useEffect(() => {
    console.log(`Route changed to: ${location.pathname} with language: ${language}`);
    
    // Ensure the document has the correct language
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
      document.documentElement.dir = 'ltr'; // All supported languages use left-to-right direction
    }
  }, [language, location.pathname]);
  
  return (
    <LayoutProvider>
      <div className="flex flex-col min-h-screen bg-white">
        <Header />
        <main className="flex-grow pt-16 sm:pt-20 md:pt-24 pb-8 sm:pb-12 md:pb-16" key={contentKey}>
          <div className="min-h-[calc(100vh-8rem)] sm:min-h-[calc(100vh-10rem)] md:min-h-[calc(100vh-12rem)]">
            {children}
          </div>
        </main>
        <Footer />
        <SessionTimeoutWarning />
      </div>
    </LayoutProvider>
  );
});

Layout.displayName = 'Layout';

export default Layout;
